import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  patentDraftService,
  type DPACostEstimate,
  type Embodiment,
  type PatentDraft,
  type ReferenceSign,
  type StepResult,
} from '../services/patentDraftService';

type StepId = 'step2' | 'step3' | 'step5_1' | 'step5_2' | 'step5_3' | 'step5_4' | 'step5_5' | 'step6' | 'step7';

interface PatentDraftState {
  draft: PatentDraft | null;
  costEstimate: DPACostEstimate | null;
  currentStep: StepId | null;
  stepResult: StepResult | null;
  isLoading: boolean;
  isRunningStep: boolean;
  error: string | null;
}

interface PatentDraftActions {
  loadDraft: (subProjectId: string, solutionId: string) => Promise<void>;
  estimateCost: (subProjectId: string, solutionId: string) => Promise<void>;
  runStep: (subProjectId: string, solutionId: string, step: StepId) => Promise<void>;
  approveStructure: (
    subProjectId: string,
    solutionId: string,
    embodiments: Embodiment[],
    referenceSigns: ReferenceSign[],
  ) => Promise<void>;
  updateContent: (
    subProjectId: string,
    solutionId: string,
    updates: {
      title?: string;
      abstract?: string;
      description_html?: string;
      claims_section_html?: string;
    },
  ) => Promise<void>;
  deleteDraft: (subProjectId: string, solutionId: string) => Promise<void>;
  reset: () => void;
}

const initialState: PatentDraftState = {
  draft: null,
  costEstimate: null,
  currentStep: null,
  stepResult: null,
  isLoading: false,
  isRunningStep: false,
  error: null,
};

export const usePatentDraftStore = create<PatentDraftState & PatentDraftActions>()(
  devtools(
    (set, get) => ({
      ...initialState,

      loadDraft: async (subProjectId, solutionId) => {
        set({ isLoading: true, error: null });
        try {
          const draft = await patentDraftService.getDraft(subProjectId, solutionId);
          set({ draft, isLoading: false });
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : 'Failed to load patent draft';
          set({ error: msg, isLoading: false });
        }
      },

      estimateCost: async (subProjectId, solutionId) => {
        set({ error: null });
        try {
          const costEstimate = await patentDraftService.estimateCost(subProjectId, solutionId);
          set({ costEstimate });
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : 'Failed to estimate cost';
          set({ error: msg });
        }
      },

      runStep: async (subProjectId, solutionId, step) => {
        set({ isRunningStep: true, currentStep: step, stepResult: null, error: null });
        try {
          const result = await patentDraftService.runStep(subProjectId, solutionId, step);
          set({ stepResult: result, isRunningStep: false });
          // Reload draft to get updated state
          const draft = await patentDraftService.getDraft(subProjectId, solutionId);
          set({ draft });
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : `Step ${step} failed`;
          set({ error: msg, isRunningStep: false });
        }
      },

      approveStructure: async (subProjectId, solutionId, embodiments, referenceSigns) => {
        const { draft } = get();
        if (!draft) return;
        set({ isLoading: true, error: null });
        try {
          const updated = await patentDraftService.updateStructure(
            subProjectId, solutionId, embodiments, referenceSigns, draft.version,
          );
          set({ draft: updated, isLoading: false });
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : 'Failed to update structure';
          set({ error: msg, isLoading: false });
        }
      },

      updateContent: async (subProjectId, solutionId, updates) => {
        const { draft } = get();
        if (!draft) return;
        set({ isLoading: true, error: null });
        try {
          const updated = await patentDraftService.updateContent(
            subProjectId, solutionId, draft.version, updates,
          );
          set({ draft: updated, isLoading: false });
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : 'Failed to update content';
          set({ error: msg, isLoading: false });
        }
      },

      deleteDraft: async (subProjectId, solutionId) => {
        set({ isLoading: true, error: null });
        try {
          await patentDraftService.deleteDraft(subProjectId, solutionId);
          set({ ...initialState });
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : 'Failed to delete draft';
          set({ error: msg, isLoading: false });
        }
      },

      reset: () => set(initialState),
    }),
    { name: 'patent-draft-store' },
  ),
);
